import produce from 'immer';

import { ConnectionType, connect } from '@swf/core/api/connect';
import { Brand } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { RewardsStateModel } from '@swf/core/types/RewardsLegacy';

interface RewardsModel extends ModelConfig<RewardsState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

interface ExchangeResponse {
  id: number;
  type: string;
  success: boolean;
  error?: string;
}

interface ExchangeParams {
  response: ExchangeResponse | null;
}

export interface RewardsState {
  current?: RewardsStateModel;
  queue: RewardsStateModel[];
  optedIn?: boolean;
  exchange: ExchangeParams;
}

const ANIMATION_TIMEOUT = 1650;
let animationQueueTimer: number;

const defaultState = {
  queue: [],
  exchange: { response: null }
};

export enum RewardsBrand {
  FoxBet = 'FOX Bet',
  SkyBet = 'Sky Bet',
  PokerStars = 'PokerStars'
}

const reducers = {
  add: (state: RewardsState, payload: RewardsStateModel): RewardsState =>
    produce(state, (draft) => {
      draft.queue.push(payload);
    }),
  remove: (state: RewardsState): RewardsState =>
    produce(state, (draft) => {
      draft.current = draft.queue.shift();
    }),
  'session/clear': (): RewardsState => defaultState,
  setOptIn: (state: RewardsState, payload: boolean): RewardsState => ({
    ...state,
    optedIn: payload
  }),
  setExchangeStatus: (state: RewardsState, payload: ExchangeParams): RewardsState =>
    produce(state, (draft) => {
      draft.exchange = payload;
    })
};

const effects = {
  async processItem(payload: undefined, { rootState, dispatch }: EffectParams) {
    const {
      rewardsLegacy: { queue }
    } = rootState;

    if (queue.length) {
      dispatch.rewardsLegacy.remove();
      dispatch.rewardsLegacy.processQueue();
    } else {
      clearTimeout(animationQueueTimer);
      animationQueueTimer = 0;
    }
  },
  async processQueue(payload: undefined, { dispatch }: EffectParams) {
    animationQueueTimer = window.setTimeout(() => {
      dispatch.rewardsLegacy.processItem();
    }, ANIMATION_TIMEOUT);
  },
  async enqueue(payload: RewardsStateModel, { dispatch }: EffectParams) {
    dispatch.rewardsLegacy.add(payload);
    if (!animationQueueTimer) {
      dispatch.rewardsLegacy.processQueue();
    }
  },
  async optIn(payload: undefined, { dispatch, request }: EffectParams) {
    try {
      await request({ service: 'cvl.optIn' });
      dispatch.rewardsLegacy.setOptIn(true);
    } catch (e) {
      // TODO: display error message
      dispatch.rewardsLegacy.setOptIn(false);
    }
  },
  async exchange(payload: undefined, { dispatch, rootState, request }: EffectParams) {
    const options = select.cms.getRequestOptions(rootState);
    const { body } = await request({
      ...options,
      service: 'starsrewards',
      body: { id: 1, type: 'exchange' },
      connection: connect(ConnectionType.Rewards)
    });
    dispatch.rewardsLegacy.setExchangeStatus({ response: body });
  }
};

const selectors = (slice: Slicer<RewardsState>, createSelector: SelectorCreator) => ({
  get() {
    return slice((state) => (state.current || {}) as Partial<RewardsStateModel>);
  },
  getChestProps() {
    return slice((state) =>
      Object.assign(
        {
          tierId: 0,
          currentPoints: 0,
          targetPoints: 0,
          expanded: false,
          cvlConfig: { cvl2: false }
        },
        state.current
      )
    );
  },
  getExchangeStatus() {
    return slice((state) => state.exchange);
  },
  getRewardsBrand() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brand) => {
        switch (brand) {
          case Brand.FoxBet:
            return RewardsBrand.FoxBet;
          case Brand.SkyBet:
            return RewardsBrand.SkyBet;
          default:
            return RewardsBrand.PokerStars;
        }
      }
    );
  }
});

const rewardsLegacy: RewardsModel = {
  state: defaultState,
  reducers,
  effects,
  selectors
};

export default rewardsLegacy;
